import { useState, useEffect, useCallback, useMemo } from "react";

import { getBackend } from "@/lib/backend/backend2";
import { getLineGroupForUser } from "@/lib/businessLogic";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { fromShiftTag, weekToDayShift, shiftNumber, stringCompare, isoDateFromShiftTag } from "@/lib/util";
import { SHIFT_EXCHANGE_REQUEST_STATUS, SHIFT_EXCHANGE_LISTING_STATUS } from "@/lib/ManufacturingConsts.generated";

import type {
    IGetShiftExchangeListingsRes,
    IGetShiftExchangeRequestsRes,
    IGetPeopleShiftsTableReq,
    IPersonData,
    IShiftExchangeListing,
    IShiftExchangeRequest,
    IShiftListing,
    IShiftPersonLineRec,
    ICompetenceData,
    ILineGroupData,
} from "@/lib/backend/manufacturing2.generated.types";

import { ShiftExchangeListingStatusEnum, ShiftExchangeRequestStatusEnum } from "@/lib/ManufacturingConsts.generated";

// ---------------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------------

export const WORKER_EXCHANGE_TABS = {
    OFFERINGS: "offerings",
    BOOKINGS: "bookings",
} as const;

export const WORKER_EXCHANGE_VIEWS = {
    // Offerings tab views
    MY_AVAILABILITIES: "myAvailabilities",
    MY_OFFERINGS: "myOfferings",
    // Bookings tab views
    OTHER_OFFERINGS: "otherOfferings",
    MY_BOOKINGS: "myBookings",
} as const;

// ---------------------------------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------------------------------

export type WorkerExchangeTab = (typeof WORKER_EXCHANGE_TABS)[keyof typeof WORKER_EXCHANGE_TABS];

export type WorkerExchangeView = (typeof WORKER_EXCHANGE_VIEWS)[keyof typeof WORKER_EXCHANGE_VIEWS];

export type LineGroupInfo = {
    id: number;
    externalId: string;
};

export type UseWorkerExchangeDataResult = {
    myListings: IShiftExchangeListing[];
    otherListings: IShiftExchangeListing[];
    outgoingRequests: IShiftExchangeRequest[];
    incomingRequests: IShiftExchangeRequest[];
    competences: ICompetenceData[];
    peopleSchedule: IShiftPersonLineRec[];
    lineGroups: ILineGroupData[];
    loadingState: LoadingState;
    error: string;
    refetchListings: () => Promise<void>;
    refetchRequests: () => Promise<void>;
    refetchCompetences: () => Promise<void>;
    refetchPeopleSchedule: (params: IGetPeopleShiftsTableReq) => Promise<void>;
    refetchLineGroups: () => Promise<void>;
    refetchAll: () => Promise<void>;
};

export type Availability = {
    person: IPersonData;
    shiftLineId: number;
    date: Date;
    shiftNumber: number;
    shiftTag: string;
};

export type ExchangeListingStatus = ShiftExchangeListingStatusEnum | "pending";

export type ExchangeListingInfo = {
    listingId: number;
    shiftTag: string;
    status: ExchangeListingStatus;
};

export type ExchangeListingLookup = Map<number, Map<number, ExchangeListingInfo>>;

export type LoadingState = {
    all: boolean;
    listings: boolean;
    requests: boolean;
    competences: boolean;
    peopleSchedule: boolean;
    lineGroups: boolean;
};

export type MyOfferingsRow = {
    listingId: number;
    requestId: number | null;
    status: ExchangeListingStatus;
    personId: number;
    personExternalId: string;
    personName: string;
    destinationLineGroupTitle: string;
    destinationLineGroupId: number;
    competences: ICompetenceData[];
    date: Date;
    shiftNumber: number;
    shiftLineId: number;
};

export type AvailabilityRow = {
    personId: number;
    personExternalId: string;
    personName: string;
    competences: ICompetenceData[];
    date: Date;
    shiftNumber: number;
    shiftLineId: number;
};

export type OtherOfferingsRow = {
    listingId: number;
    personId: number;
    personExternalId: string;
    personName: string;
    sourceLineGroupTitle: string;
    sourceLineGroupId: number;
    competences: ICompetenceData[];
    date: Date;
    shiftNumber: number;
    shiftLineId: number;
};

export type MyBookingsRow = {
    requestId: number;
    listingId: number;
    status: ShiftExchangeRequestStatusEnum;
    personId: number;
    personExternalId: string;
    personName: string;
    sourceLineGroupTitle: string;
    sourceLineGroupId: number;
    competences: ICompetenceData[];
    date: Date;
    shiftNumber: number;
    shiftLineId: number;
};

// ---------------------------------------------------------------------------------------------------------------------
// Unique values for filters
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Gets unique shift values from an array of rows that have a shiftNumber property.
 * Converts shift numbers (0, 1, 2) to display values (1, 2, 3) and returns them sorted.
 */
export const getUniqueShiftValues = <T extends { shiftNumber: number }>(rows: T[]): string[] => {
    const shifts = new Set(rows.map(row => (row.shiftNumber + 1).toString()));
    return Array.from(shifts).sort();
};

/**
 * Gets unique competence codes from an array of rows that have a competences property.
 * Extracts all competence codes from all rows and returns them sorted.
 */
export const getUniqueCompetenceCodes = <T extends { competences: ICompetenceData[] }>(rows: T[]): string[] => {
    const codes = new Set<string>();
    let containsNoCompetences = false;
    rows.forEach(row => {
        if (row.competences.length === 0) {
            containsNoCompetences = true;
        } else {
            row.competences.forEach(competence => {
                if (competence.code) {
                    codes.add(competence.code);
                }
            });
        }
    });
    const sortedCodes = Array.from(codes).sort();
    // Add "No competences" to the end of the array if it exists
    if (containsNoCompetences) {
        sortedCodes.push(`(${translate("WorkerExchange.noCompetences", "No competences")})`);
    }
    return sortedCodes;
};

/**
 * Gets unique string values from an array of rows by extracting a property by name.
 * Filters out empty/null values and returns them sorted.
 */
export const getUniqueValues = <T, K extends keyof T>(rows: T[], property: K): string[] => {
    const values = new Set(rows.map(row => String(row[property])).filter(Boolean));
    return Array.from(values).sort();
};

/**
 * Gets the translated label for a status value.
 * @param status - The status value (listing status or request status).
 * @returns The translated label for the status, or the original value if no label is found.
 */
export const getStatusLabel = (status: string): string => {
    const statusLabels: Record<string, string> = {
        [SHIFT_EXCHANGE_LISTING_STATUS.available]: translate("WorkerExchange.status.available", "Available"),
        [SHIFT_EXCHANGE_LISTING_STATUS.cancelled]: translate("WorkerExchange.status.canceled", "Canceled"),
        [SHIFT_EXCHANGE_REQUEST_STATUS.pending]: translate("WorkerExchange.status.pending", "Pending"),
        [SHIFT_EXCHANGE_REQUEST_STATUS.confirmed]: translate("WorkerExchange.status.confirmed", "Confirmed"),
        [SHIFT_EXCHANGE_REQUEST_STATUS.rejected]: translate("WorkerExchange.status.rejected", "Rejected"),
    };
    return statusLabels[status] ?? status;
};

// ---------------------------------------------------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Compares two exchange listing statuses.
 */

const compareExchangeListingStatus = (a: ExchangeListingStatus, b: ExchangeListingStatus): number => {
    const statusOrder: ExchangeListingStatus[] = [
        SHIFT_EXCHANGE_REQUEST_STATUS.pending,
        SHIFT_EXCHANGE_LISTING_STATUS.confirmed,
        SHIFT_EXCHANGE_LISTING_STATUS.available,
        SHIFT_EXCHANGE_LISTING_STATUS.cancelled,
    ] as ExchangeListingStatus[];
    return statusOrder.indexOf(a) - statusOrder.indexOf(b);
};

/**
 * Compares two exchange request statuses.
 */
const compareExchangeRequestStatus = (a: ShiftExchangeRequestStatusEnum, b: ShiftExchangeRequestStatusEnum): number => {
    const statusOrder: ShiftExchangeRequestStatusEnum[] = [
        SHIFT_EXCHANGE_REQUEST_STATUS.pending,
        SHIFT_EXCHANGE_REQUEST_STATUS.confirmed,
        SHIFT_EXCHANGE_REQUEST_STATUS.rejected,
    ];
    return statusOrder.indexOf(a) - statusOrder.indexOf(b);
};

/**
 * Gets a person's competences as an array of competence objects.
 * @param competenceIds - Array of competence IDs, or undefined if none.
 * @param competencesById - Map of competence ID to competence data.
 * @returns Array of competence objects, or empty array if none.
 */
export const getCompetences = (
    competenceIds: number[] | undefined,
    competencesById: Map<number, ICompetenceData>
): ICompetenceData[] => {
    if (!competenceIds) {
        return [];
    }
    return competenceIds
        .map(id => competencesById.get(id))
        .filter((competence): competence is ICompetenceData => competence !== undefined);
};

/**
 * Creates a lookup map of active exchange listings grouped by person ID and shift line ID.
 */
export const buildExchangeListingLookup = (
    listings: IShiftExchangeListing[],
    requests: IShiftExchangeRequest[] = []
): ExchangeListingLookup => {
    const pendingListingIds = new Set<number>(
        requests
            .filter(request => request.status === SHIFT_EXCHANGE_REQUEST_STATUS.pending)
            .map(request => request.listing_id)
    );

    const lookup: ExchangeListingLookup = new Map();
    for (const listing of listings) {
        const shifts = [...listing.shifts].sort((a, b) => a.shift_tag.localeCompare(b.shift_tag));
        for (const shift of shifts) {
            const status = pendingListingIds.has(shift.listing_id)
                ? SHIFT_EXCHANGE_REQUEST_STATUS.pending
                : shift.listing_status;
            if (status === SHIFT_EXCHANGE_LISTING_STATUS.cancelled) {
                continue;
            }

            const listingInfo = lookup.get(listing.person_id) ?? new Map<number, ExchangeListingInfo>();
            listingInfo.set(shift.shift_line_id, {
                listingId: shift.listing_id,
                shiftTag: shift.shift_tag,
                status: status as ExchangeListingStatus,
            });
            lookup.set(listing.person_id, listingInfo);
        }
    }

    return lookup;
};

/**
 * Gets the listing key for an availability.
 */
export const getAvailabilityKey = (personId: number, shiftLineId: number): string => {
    return `${personId}-${shiftLineId}`;
};

/**
 * Generates a key for tracking listing cancellation submission state.
 */
export const getListingKey = (listingId: number): string => {
    return `listing-${listingId}`;
};

/**
 * Generates a key for tracking request confirmation submission state.
 */
export const getRequestConfirmKey = (requestId: number): string => {
    return `request-confirm-${requestId}`;
};

/**
 * Generates a key for tracking request rejection submission state.
 */
export const getRequestRejectKey = (requestId: number): string => {
    return `request-reject-${requestId}`;
};

/**
 * Checks if an exchange listing is active (exists and not cancelled).
 */
export const isActiveExchangeListing = (info?: ExchangeListingInfo): boolean => {
    return info !== undefined && info.status !== SHIFT_EXCHANGE_LISTING_STATUS.cancelled;
};

/**
 * Adds a shift exchange listing for a person with one or more shifts.
 */
export const addExchangeListing = async (
    lineGroupId: number,
    personId: number,
    shiftLineIds: number[]
): Promise<void> => {
    await getBackend().manufacturing.addShiftExchangeListing({
        line_group_id: lineGroupId,
        listing: {
            person_id: personId,
            shift_line_ids: shiftLineIds,
        },
    });
};

/**
 * Cancels a confirmed shift exchange listing.
 */
export const cancelExchangeListing = async (lineGroupId: number, listingId: number): Promise<void> => {
    await getBackend().manufacturing.cancelShiftExchangeListing({
        line_group_id: lineGroupId,
        listing_id: listingId,
    });
};

/**
 * Deletes a shift exchange listing.
 */
export const deleteExchangeListing = async (lineGroupId: number, listingId: number): Promise<void> => {
    await getBackend().manufacturing.deleteShiftExchangeListing({
        line_group_id: lineGroupId,
        listing_id: listingId,
    });
};

/**
 * Accepts a shift exchange request.
 */
export const acceptExchangeRequest = async (lineGroupId: number, requestId: number): Promise<void> => {
    await getBackend().manufacturing.acceptShiftExchangeRequest({
        line_group_id: lineGroupId,
        request_id: requestId,
    });
};

/**
 * Rejects a shift exchange request.
 */
export const rejectExchangeRequest = async (lineGroupId: number, requestId: number): Promise<void> => {
    await getBackend().manufacturing.rejectShiftExchangeRequest({
        line_group_id: lineGroupId,
        request_id: requestId,
    });
};

/**
 * Creates a shift exchange request for a listing.
 */
export const createExchangeRequest = async (lineGroupId: number, listingId: number): Promise<void> => {
    await getBackend().manufacturing.addShiftExchangeRequest({
        line_group_id: lineGroupId,
        listing_id: listingId,
    });
};

/**
 * Cancels/deletes a shift exchange request.
 */
export const cancelExchangeRequest = async (lineGroupId: number, requestId: number): Promise<void> => {
    await getBackend().manufacturing.deleteShiftExchangeRequest({
        line_group_id: lineGroupId,
        request_id: requestId,
    });
};

/**
 * Gets list of people available for exchange per shift.
 * A person is available if they are assigned to poolLine but not assigned
 * to any line in peopleSchedule for the same shift.
 *
 * @param lineGroupExternalId - External ID of the line group to filter people by ted_id.
 * @param people - List of all people.
 * @param poolLine - The pool line containing available people.
 * @param peopleSchedule - Schedule of people assigned to shifts on production lines.
 * @param listingLookup - Lookup of listings by person ID and shift line ID.
 * @returns List of availabilities (person + date + shift).
 */
export const getExchangeAvailabilities = (
    lineGroupExternalId: string,
    people: IPersonData[],
    poolLine: IShiftPersonLineRec,
    peopleSchedule: IShiftPersonLineRec[],
    listingLookup?: ExchangeListingLookup
): Availability[] => {
    // Create a map of people by UUID for quick lookup (filtered by line group)
    const peopleByUuid = new Map<string, IPersonData>(
        people.filter(person => person.ted_id === lineGroupExternalId).map(person => [person.uuid, person])
    );

    const scheduledByDay = schedulePeopleByDay(peopleSchedule);

    // Find available people: in pool but not scheduled
    const availabilities: Availability[] = [];
    const currentShiftTag = shiftNumber(new Date()).shift_tag;

    for (const shift of poolLine.shifts) {
        if (shift.shift_tag < currentShiftTag) {
            continue;
        }

        const isoDate = isoDateFromShiftTag(shift.shift_tag);
        const scheduledUuids = scheduledByDay.get(isoDate) ?? new Set();

        for (const poolPerson of shift.persons) {
            // Skip if person is scheduled on another line
            if (scheduledUuids.has(poolPerson.uuid)) {
                continue;
            }

            // Get full person data (also validates ted_id match)
            const person = peopleByUuid.get(poolPerson.uuid);
            if (!person) {
                continue;
            }

            // Skip if person has an active listing
            if (isActiveExchangeListing(listingLookup?.get(person.id)?.get(shift.shift_line_id))) {
                continue;
            }

            availabilities.push({
                person,
                shiftLineId: shift.shift_line_id,
                date: fromShiftTag(shift.shift_tag),
                shiftNumber: weekToDayShift(shift.shift_number),
                shiftTag: shift.shift_tag,
            });
        }
    }

    return availabilities;
};

/**
 * Builds a set of scheduled person UUIDs per the date of a shift.
 * @param peopleSchedule - The schedule of people assigned to shifts on production lines.
 * @returns A map of shift dates (YYYY-MM-DD) to sets of person UUIDs.
 *
 * If a worker is scheduled for any shift on the same day, they are considered scheduled for that day.
 *
 * Example:
 * {
 *   "2026-04-14": new Set(["uuid1", "uuid2"]),
 *   "2026-04-15": new Set(["uuid3", "uuid4"]),
 * }
 */
const schedulePeopleByDay = (peopleSchedule: IShiftPersonLineRec[]): Map<string, Set<string>> => {
    const scheduledByDay = new Map<string, Set<string>>();
    for (const line of peopleSchedule) {
        for (const shift of line.shifts) {
            const isoDate = isoDateFromShiftTag(shift.shift_tag);
            const scheduled = scheduledByDay.get(isoDate) ?? new Set();
            for (const person of shift.persons) {
                scheduled.add(person.uuid);
            }
            scheduledByDay.set(isoDate, scheduled);
        }
    }
    return scheduledByDay;
};

/**
 * Gets the active tab from a view.
 * @param view - The current view.
 * @returns The tab that contains this view.
 */
export const getTabFromView = (view: WorkerExchangeView): WorkerExchangeTab => {
    return view === WORKER_EXCHANGE_VIEWS.MY_AVAILABILITIES || view === WORKER_EXCHANGE_VIEWS.MY_OFFERINGS
        ? WORKER_EXCHANGE_TABS.OFFERINGS
        : WORKER_EXCHANGE_TABS.BOOKINGS;
};

/**
 * Gets the default view based on whether a person is selected.
 * @param selectedPersonId - The selected person ID, or null if no person is selected.
 * @returns The default view to show.
 */
export const getDefaultView = (selectedPersonId: number | null): WorkerExchangeView => {
    return selectedPersonId ? WORKER_EXCHANGE_VIEWS.MY_AVAILABILITIES : WORKER_EXCHANGE_VIEWS.MY_OFFERINGS;
};

/**
 * Builds table rows for the "My offerings" table from listings and incoming requests.
 * If a listing has no requests, it shows one row with "Available" status.
 * If a listing has requests, it shows one row per request with the request's status.
 */
export const buildMyOfferingsRows = (
    myListings: IShiftExchangeListing[],
    incomingRequests: IShiftExchangeRequest[],
    people: IPersonData[],
    competencesById: Map<number, ICompetenceData>,
    lineGroups: ILineGroupData[]
): MyOfferingsRow[] => {
    const rows: MyOfferingsRow[] = [];

    const peopleById = new Map<number, IPersonData>(people.map(person => [person.id, person]));
    const lineGroupsById = new Map<number, ILineGroupData>(lineGroups.map(lineGroup => [lineGroup.id, lineGroup]));

    // Group requests by listing_id
    const requestsByListingId = new Map<number, IShiftExchangeRequest[]>();
    for (const request of incomingRequests) {
        if (request.status === SHIFT_EXCHANGE_REQUEST_STATUS.rejected) {
            continue;
        }

        const listingId = request.listing_id;
        let requests = requestsByListingId.get(listingId);
        if (!requests) {
            requests = [];
            requestsByListingId.set(listingId, requests);
        }

        requests.push(request);
    }

    // Process each listing
    for (const listing of myListings) {
        const person = peopleById.get(listing.person_id);
        if (!person) {
            continue;
        }

        const competences = getCompetences(person.competences, competencesById);

        // Process each shift in the listing
        for (const shift of listing.shifts) {
            // Skip cancelled shifts
            if (shift.listing_status === SHIFT_EXCHANGE_LISTING_STATUS.cancelled) {
                continue;
            }

            const date = fromShiftTag(shift.shift_tag);
            const { shift_day } = shiftNumber(date);

            const requestsForThisListing = requestsByListingId.get(shift.listing_id) ?? [];

            if (requestsForThisListing.length === 0) {
                // No requests - show one row with "Available" status
                rows.push({
                    listingId: shift.listing_id,
                    requestId: null,
                    status: shift.listing_status as ExchangeListingStatus,
                    personId: person.id,
                    personExternalId: person.external_id,
                    personName: person.name,
                    destinationLineGroupTitle: "", // Destination is not known for available listings
                    destinationLineGroupId: 0,
                    competences: competences,
                    date: date,
                    shiftNumber: shift_day,
                    shiftLineId: shift.shift_line_id,
                });
            } else {
                // Has requests - show one row per request
                for (const request of requestsForThisListing) {
                    rows.push({
                        listingId: shift.listing_id,
                        requestId: request.request_id,
                        status: request.status as ExchangeListingStatus,
                        personId: person.id,
                        personExternalId: person.external_id,
                        personName: person.name,
                        destinationLineGroupTitle: lineGroupsById.get(request.requester_linegroup_id)?.title ?? "",
                        destinationLineGroupId: request.requester_linegroup_id,
                        competences: competences,
                        date: date,
                        shiftNumber: shift_day,
                        shiftLineId: shift.shift_line_id,
                    });
                }
            }
        }
    }

    return rows.sort(
        (a, b) =>
            compareExchangeListingStatus(a.status, b.status) ||
            a.date.getTime() - b.date.getTime() ||
            stringCompare(a.personName, b.personName)
    );
};

/**
 * Builds table rows for the "My availabilities" table from availabilities.
 */
export const buildMyAvailabilitiesRows = (
    availabilities: Availability[],
    competencesById: Map<number, ICompetenceData>
): AvailabilityRow[] => {
    return availabilities
        .map(availability => {
            const competences = getCompetences(availability.person.competences, competencesById);

            return {
                personId: availability.person.id,
                personExternalId: availability.person.external_id,
                personName: availability.person.name,
                competences: competences,
                date: availability.date,
                shiftNumber: availability.shiftNumber,
                shiftLineId: availability.shiftLineId,
            };
        })
        .sort((a, b) => a.date.getTime() - b.date.getTime() || stringCompare(a.personName, b.personName));
};

/**
 * Builds table rows for the "Other offerings" table from other listings and outgoing requests.
 * Checks if there's already a pending or confirmed request for each listing.
 */
export const buildOtherOfferingsRows = (
    otherListings: IShiftExchangeListing[],
    outgoingRequests: IShiftExchangeRequest[],
    people: IPersonData[],
    competencesById: Map<number, ICompetenceData>,
    lineGroups: ILineGroupData[]
): OtherOfferingsRow[] => {
    const rows: OtherOfferingsRow[] = [];

    const peopleById = new Map<number, IPersonData>(people.map(person => [person.id, person]));
    const lineGroupsByExternalId = new Map<string, ILineGroupData>(
        lineGroups.map(lineGroup => [lineGroup.external_id, lineGroup])
    );

    // Build a set of listing IDs that have active requests (pending or confirmed)
    const activeRequestListingIds = new Set<number>();
    for (const request of outgoingRequests) {
        if (
            request.status === SHIFT_EXCHANGE_REQUEST_STATUS.pending ||
            request.status === SHIFT_EXCHANGE_REQUEST_STATUS.confirmed
        ) {
            activeRequestListingIds.add(request.listing_id);
        }
    }

    // Process each listing
    for (const listing of otherListings) {
        const person = peopleById.get(listing.person_id);
        if (!person) {
            continue;
        }

        const competences = getCompetences(person.competences, competencesById);

        // Process each shift in the listing
        for (const shift of listing.shifts) {
            // Skip cancelled shifts
            if (shift.listing_status === SHIFT_EXCHANGE_LISTING_STATUS.cancelled) {
                continue;
            }

            // Skip shifts that already have an active request (pending or confirmed)
            if (activeRequestListingIds.has(shift.listing_id)) {
                continue;
            }

            const date = fromShiftTag(shift.shift_tag);
            const { shift_day } = shiftNumber(date);
            const sourceLineGroup = lineGroupsByExternalId.get(person.ted_id);

            rows.push({
                listingId: shift.listing_id,
                personId: person.id,
                personExternalId: person.external_id,
                personName: person.name,
                sourceLineGroupTitle: sourceLineGroup?.title ?? "",
                sourceLineGroupId: sourceLineGroup?.id ?? 0,
                competences: competences,
                date: date,
                shiftNumber: shift_day,
                shiftLineId: shift.shift_line_id,
            });
        }
    }

    return rows.sort((a, b) => a.date.getTime() - b.date.getTime() || stringCompare(a.personName, b.personName));
};

/**
 * Builds table rows for the "My bookings" table from outgoing requests and other listings.
 * Matches requests with listings to get person and shift information.
 */
export const buildMyBookingsRows = (
    outgoingRequests: IShiftExchangeRequest[],
    otherListings: IShiftExchangeListing[],
    people: IPersonData[],
    competencesById: Map<number, ICompetenceData>,
    lineGroups: ILineGroupData[]
): MyBookingsRow[] => {
    const rows: MyBookingsRow[] = [];

    const peopleById = new Map<number, IPersonData>(people.map(person => [person.id, person]));
    // Get source line group name (where the offering is from)
    // person.ted_id is the line group external ID
    const lineGroupsByExternalId = new Map<string, ILineGroupData>(
        lineGroups.map(lineGroup => [lineGroup.external_id, lineGroup])
    );

    // Build a map of listings by listing_id for quick lookup
    const listingsByListingId = new Map<number, { listing: IShiftExchangeListing; shift: IShiftListing }>();
    for (const listing of otherListings) {
        for (const shift of listing.shifts) {
            listingsByListingId.set(shift.listing_id, { listing, shift });
        }
    }

    // Process each outgoing request
    for (const request of outgoingRequests) {
        const listingInfo = listingsByListingId.get(request.listing_id);
        if (!listingInfo) {
            continue;
        }

        const { listing, shift } = listingInfo;
        const person = peopleById.get(listing.person_id);
        if (!person) {
            continue;
        }

        const competences = getCompetences(person.competences, competencesById);
        const date = fromShiftTag(shift.shift_tag);
        const { shift_day } = shiftNumber(date);
        const sourceLineGroup = lineGroupsByExternalId.get(person.ted_id);

        rows.push({
            requestId: request.request_id,
            listingId: request.listing_id,
            status: request.status as ShiftExchangeRequestStatusEnum,
            personId: person.id,
            personExternalId: person.external_id,
            personName: person.name,
            sourceLineGroupTitle: sourceLineGroup?.title ?? "",
            sourceLineGroupId: sourceLineGroup?.id ?? 0,
            competences: competences,
            date: date,
            shiftNumber: shift_day,
            shiftLineId: shift.shift_line_id,
        });
    }

    return rows.sort(
        (a, b) =>
            compareExchangeRequestStatus(a.status, b.status) ||
            a.date.getTime() - b.date.getTime() ||
            stringCompare(a.personName, b.personName)
    );
};

// ---------------------------------------------------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------------------------------------------------

/**
 * Hook for fetching and managing worker exchange data (listings, requests, competences, and people shifts table).
 * Fetches listings, requests, and competences in parallel on mount, with separate refetch functions for granular updates.
 * People schedule must be fetched explicitly with parameters via refetchPeopleSchedule.
 *
 * @param lineGroupUuid - The UUID of the line group.
 * @returns Object containing listings, requests, competences, peopleSchedule, loading states, error, and refetch functions.
 */
export function useWorkerExchangeData(lineGroupUuid: string): UseWorkerExchangeDataResult {
    // Separate data states
    const [listings, setListings] = useState<IGetShiftExchangeListingsRes>({
        my_listings: [],
        other_listings: [],
    });
    const [requests, setRequests] = useState<IGetShiftExchangeRequestsRes>({
        outgoing_requests: [],
        incoming_requests: [],
    });
    const [competences, setCompetences] = useState<ICompetenceData[]>([]);
    const [peopleSchedule, setPeopleSchedule] = useState<IShiftPersonLineRec[]>([]);
    const [lineGroups, setLineGroups] = useState<ILineGroupData[]>([]);

    // Separate loading states (for granular updates)
    const [loadingState, setLoadingState] = useState<LoadingState>({
        all: true,
        listings: false,
        requests: false,
        competences: false,
        peopleSchedule: false,
        lineGroups: false,
    });

    // Single shared error
    const [error, setError] = useState("");

    const lineGroupId = useMemo(() => getLineGroupForUser(lineGroupUuid)?.id ?? 0, [lineGroupUuid]);

    // Generic fetcher helper with optional validation
    const createFetcher = <T>(
        loadingKey: keyof LoadingState,
        fetcher: () => Promise<T>,
        setter: (data: T) => void,
        errorMessage: string,
        validate?: () => boolean
    ) => {
        return async () => {
            // Run validation before setting loading state
            if (validate && !validate()) {
                setError(translate("common.error", "An error occurred while fetching data"));
                setLoadingState(prev => ({ ...prev, [loadingKey]: false }));
                return;
            }

            setLoadingState(prev => ({ ...prev, [loadingKey]: true }));
            setError("");

            try {
                const result = await fetcher();
                setter(result);
            } catch (err) {
                setError(translate("common.error", "An error occurred while fetching data"));
                console.error(errorMessage, err);
            } finally {
                setLoadingState(prev => ({ ...prev, [loadingKey]: false }));
            }
        };
    };

    // Fetch only listings
    const fetchListings = useCallback(
        createFetcher(
            "listings",
            () =>
                getBackend().manufacturing.getShiftExchangeListings({
                    line_group_id: lineGroupId,
                }),
            setListings,
            "Failed to fetch listings:",
            () => lineGroupId !== 0
        ),
        [lineGroupId]
    );

    // Fetch only requests
    const fetchRequests = useCallback(
        createFetcher(
            "requests",
            () =>
                getBackend().manufacturing.getShiftExchangeRequests({
                    line_group_id: lineGroupId,
                }),
            setRequests,
            "Failed to fetch requests:",
            () => lineGroupId !== 0
        ),
        [lineGroupId]
    );

    // Fetch competences
    const fetchCompetences = useCallback(
        createFetcher(
            "competences",
            async () => {
                const result = await getBackend().manufacturing.getCompetences({});
                return result.competences;
            },
            setCompetences,
            "Failed to fetch competences:"
        ),
        []
    );

    // Fetch people schedule
    const fetchPeopleSchedule = useCallback(
        (params: IGetPeopleShiftsTableReq) =>
            createFetcher(
                "peopleSchedule",
                async () => {
                    const result = await getBackend().manufacturing.getPeopleShiftsTable(params);
                    return result.lines;
                },
                setPeopleSchedule,
                "Failed to fetch people schedule:"
            )(),
        []
    );

    // Fetch line groups
    const fetchLineGroups = useCallback(
        createFetcher(
            "lineGroups",
            async () => {
                const { line_groups } = await getBackend().manufacturing.getLineGroups({});
                return line_groups;
            },
            setLineGroups,
            "Failed to fetch line groups:"
        ),
        []
    );

    // Fetch both in parallel (initial load)
    const fetchAll = useCallback(async () => {
        setError("");
        setLoadingState(prev => ({ ...prev, all: true }));
        await Promise.all([fetchListings(), fetchRequests(), fetchCompetences(), fetchLineGroups()]);
        setLoadingState(prev => ({ ...prev, all: false }));
    }, [fetchListings, fetchRequests, fetchCompetences, fetchLineGroups]);

    // Initial load
    useEffect(() => {
        fetchAll().catch(() => {
            // Errors are surfaced via setError in createFetcher / component state
        });
    }, [fetchAll]);

    return {
        myListings: listings.my_listings,
        otherListings: listings.other_listings,
        outgoingRequests: requests.outgoing_requests,
        incomingRequests: requests.incoming_requests,
        competences,
        peopleSchedule,
        lineGroups,
        loadingState,
        error,
        refetchListings: fetchListings,
        refetchRequests: fetchRequests,
        refetchCompetences: fetchCompetences,
        refetchPeopleSchedule: fetchPeopleSchedule,
        refetchLineGroups: fetchLineGroups,
        refetchAll: fetchAll,
    };
}
